import gsap from 'gsap';
import { Container, Graphics, Point, Text } from 'pixi.js';
import { Reel } from './Reel.js';
import { gameConfig } from '../config/gameConfig.js';
import { isPrimaryPointerAction } from '../utils/pointerPrimary.js';
import { createVineSpineInstance } from '../rendering/spineLoader.js';
import { sound } from '../utils/soundManager.js';

export class ReelContainer {
  /**
   * @param {{
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;
    this.root = new Container();
    this.root.sortableChildren = true;

    /** Reel strips — stays below cabinet frame (see GameApp frame zIndex 1). */
    this.reelsLayer = new Container();
    this.reelsLayer.zIndex = 0.5;
    this.root.addChild(this.reelsLayer);

    /**
     * Expanding-wild vine spines — reel-block coordinates, moves with {@link GameApp#_gameVisualLayer}
     * (not masked like {@link #reelsLayer}).
     */
    this.expandingWildFxLayer = new Container();
    this.expandingWildFxLayer.eventMode = 'none';
    this.expandingWildFxLayer.zIndex = 3;
    this.root.addChild(this.expandingWildFxLayer);

    /** Winning cells only: reparented here so fruit + win-frame Spine draw above the cabinet frame. */
    this._symbolWinLiftLayer = new Container();
    this._symbolWinLiftLayer.eventMode = 'none';
    this._symbolWinLiftLayer.zIndex = 2;
    this.root.addChild(this._symbolWinLiftLayer);
    this.reelsMaskShape = new Graphics();
    this._maskPadRelaxed = Math.max(0, (gameConfig.ui.wellInset ?? 0) + 18);
    this._maskInsetTopBottom = 10;

    /** @type {Reel[]} */
    this.reels = [];
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    for (let i = 0; i < gameConfig.reelCount; i++) {
      const reel = new Reel({
        index: i,
        spineAssets: this.spineAssets,
        animationController: this.animationController,
      });
      reel.root.x = i * step;
      reel.root.zIndex = 2;
      this.reelsLayer.addChild(reel.root);
      this.reels.push(reel);
    }
    this._applyReelsViewportMask(this._maskPadRelaxed);

    /** Payline guides — above lifted win symbols, below win amount overlay. */
    this.allLinesOverlay = new Container();
    this.allLinesOverlay.eventMode = 'passive';
    this.allLinesOverlay.zIndex = 3;
    this.root.addChild(this.allLinesOverlay);
    this._selectedGuideLine = null;

    this.winOverlay = new Container();
    this.winOverlay.eventMode = 'none';
    this.winOverlay.zIndex = 4;
    this.root.addChild(this.winOverlay);
    this._renderAllPaylineGuides();

    /** @type {import('gsap').core.Tween | null} */
    this._winLineCycleDelay = null;
    /** @type {(() => void) | null} */
    this._winLineCycleResolve = null;
    this._winLineCycleActive = false;
    this._stopRequested = false;
  }

  clearStopRequest() {
    this._stopRequested = false;
  }

  /**
   * @param {number} [pad]
   * @param {boolean} [idleStopped] When true, tighten top mask to hide the cell above the window (idle only).
   */
  _applyReelsViewportMask(pad = this._maskPadRelaxed, idleStopped = true) {
    const safePad = Math.max(0, Number(pad) || 0);
    const baseInsetTop = Math.max(0, Number(this._maskInsetTopBottom) || 0);
    const idleTopExtra =
      idleStopped && Math.max(0, Number(gameConfig.ui.reelViewportMaskTopExtraWhenIdle) || 0);
    const insetTop = baseInsetTop + idleTopExtra;
    const bottomExtra = Math.max(0, Number(gameConfig.ui.reelViewportMaskBottomExtra) || 0);
    const insetBottom = baseInsetTop + bottomExtra;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    this.reelsMaskShape.clear();
    this.reelsMaskShape
      .rect(
        -safePad,
        -safePad + insetTop,
        reelBlockW + safePad * 2,
        reelBlockH + safePad * 2 - insetTop - insetBottom,
      )
      .fill(0xffffff);
    const maskHost = this._reelsMaskHost ?? this.root;
    if (this.reelsMaskShape.parent !== maskHost) maskHost.addChild(this.reelsMaskShape);
    this.reelsLayer.mask = this.reelsMaskShape;
  }

  setPaylineGuidesVisible(visible) {
    this.allLinesOverlay.visible = !!visible;
  }

  /**
   * @param {import('./Symbol.js').Symbol} sym
   * @param {import('./Reel.js').Reel} reel
   */
  _liftSymbolForWin(sym, reel) {
    if (!sym?.root || sym.root.parent === this._symbolWinLiftLayer) return;
    const g = sym.root.getGlobalPosition(new Point());
    reel.stripContainer.removeChild(sym.root);
    this._symbolWinLiftLayer.addChild(sym.root);
    const lp = this._symbolWinLiftLayer.toLocal(g);
    sym.root.position.set(lp.x, lp.y);
  }

  /**
   * @param {import('./Symbol.js').Symbol} sym
   * @param {import('./Reel.js').Reel} reel
   */
  _lowerSymbolFromWin(sym, reel) {
    if (!sym?.root || sym.root.parent !== this._symbolWinLiftLayer) return;
    const g = sym.root.getGlobalPosition(new Point());
    this._symbolWinLiftLayer.removeChild(sym.root);
    reel.stripContainer.addChild(sym.root);
    const lp = reel.stripContainer.toLocal(g);
    sym.root.position.set(lp.x, lp.y);
  }

  /**
   * @param {{ reel: number, row: number }} cell
   * @returns {{ x: number, y: number }}
   */
  _cellCenter(cell) {
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    const cw = gameConfig.cellWidth;
    const ch = gameConfig.cellHeight;
    return {
      x: cell.reel * step + cw / 2,
      y: cell.row * ch + ch / 2,
    };
  }

  /**
   * Full payline path (every reel), for overlay — not limited to winning run length.
   * @param {number} paylineIndex
   * @returns {{ x: number, y: number }[]}
   */
  _fullPaylinePathPoints(paylineIndex) {
    const def = gameConfig.paylines[paylineIndex];
    if (!def?.length) return [];
    return def.map((row, reel) => this._cellCenter({ reel, row }));
  }

  clearPaylineOverlay() {
    gsap.killTweensOf(this.winOverlay);
    const removed = this.winOverlay.removeChildren();
    for (const child of removed) {
      child.destroy({ children: true });
    }
    this.winOverlay.alpha = 1;
  }

  _renderAllPaylineGuides() {
    const prev = this.allLinesOverlay.removeChildren();
    for (const child of prev) {
      child.destroy({ children: true });
    }
    const t = gameConfig.theme;
    const colors = t.paylineColors ?? [t.winGlow, t.spinRing, t.goldBright];
    for (let i = 0; i < gameConfig.paylines.length; i++) {
      if (this._selectedGuideLine != null && this._selectedGuideLine !== i) continue;
      const basePts = this._fullPaylinePathPoints(i);
      const pts = this._offsetGuidePath(basePts, i);
      if (pts.length < 2) continue;
      const lineColor = colors[i % colors.length];
      const g = new Graphics();
      if (this._selectedGuideLine === i) {
        g.moveTo(pts[0].x, pts[0].y);
        for (let p = 1; p < pts.length; p++) g.lineTo(pts[p].x, pts[p].y);
        g.stroke({
          width: 12,
          color: 0xffffff,
          alpha: 0.32,
          cap: 'round',
          join: 'round',
        });
      }
      g.moveTo(pts[0].x, pts[0].y);
      for (let p = 1; p < pts.length; p++) g.lineTo(pts[p].x, pts[p].y);
      g.stroke({
        width: this._selectedGuideLine === i ? 9 : 7,
        color: lineColor,
        alpha: this._selectedGuideLine === i ? 1 : 0.72,
        cap: 'round',
        join: 'round',
      });
      const badge = new Container();
      const badgeBg = new Graphics();
      const badgeW = 54;
      const badgeH = 46;
      badgeBg.roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 14).fill({
        color: 0x020617,
        alpha: 0.86,
      });
      badgeBg.roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 14).stroke({
        width: 3,
        color: lineColor,
        alpha: 0.95,
      });
      const label = new Text({
        text: String(i + 1),
        style: {
          fontFamily: t.fontUi,
          fontSize: 30,
          fontWeight: '900',
          fill: 0xffffff,
          stroke: { color: 0x030712, width: 5 },
        },
      });
      label.anchor.set(0.5, 0.5);
      label.y = 1;
      badge.addChild(badgeBg, label);

      const topY = this._cellCenter({ reel: 0, row: 0 }).y;
      const bottomY = this._cellCenter({ reel: 0, row: 2 }).y;
      const stepY = (bottomY - topY) / 4;
      if (i < 5) {
        badge.x = pts[0].x - 56;
        badge.y = topY + i * stepY;
      } else {
        badge.x = pts[pts.length - 1].x + 56;
        badge.y = topY + (i - 5) * stepY;
      }

      badge.alpha = 1;
      badge.eventMode = 'static';
      badge.cursor = 'pointer';
      badge.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._selectedGuideLine = this._selectedGuideLine === i ? null : i;
        this._renderAllPaylineGuides();
      });
      this.allLinesOverlay.addChild(g, badge);
    }
    if (this.allLinesOverlay.parent === this.root) {
      this.root.setChildIndex(this.allLinesOverlay, this.root.children.length - 2);
    }
  }

  _offsetGuidePath(points, lineIndex) {
    if (!Array.isArray(points) || points.length < 2) return points;
    const out = [];
    const group = Math.floor(lineIndex / 2);
    const sign = lineIndex % 2 === 0 ? -1 : 1;
    const mag = Math.min(16, 3 + group * 2);
    const first = points[0];
    const last = points[points.length - 1];
    const dx = last.x - first.x;
    const dy = last.y - first.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = (-dy / len) * sign * mag;
    const ny = (dx / len) * sign * mag;
    for (const p of points) {
      out.push({ x: p.x + nx, y: p.y + ny });
    }
    return out;
  }

  /**
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hits
   * @param {{ animateEntry?: boolean }} [opts]
   * Overlay path uses the full payline (all reels); win highlights use each hit’s `cells` only.
   */
  _renderPaylineOverlayHits(hits, opts = {}) {
    const animateEntry = opts.animateEntry !== false;
    this.clearPaylineOverlay();
    if (!hits.length) return;

    const t = gameConfig.theme;
    const u = gameConfig.ui;
    const colors = t.paylineColors ?? [t.winGlow, t.spinRing, t.goldBright];

    let renderedLines = 0;
    hits.forEach((hit, hitIndex) => {
      if (hit?.displayMode === 'symbols') return;
      const pts = Array.isArray(hit.cells) && hit.cells.length > 1
        ? hit.cells.map((c) => this._cellCenter(c))
        : this._fullPaylinePathPoints(hit.paylineIndex);
      if (pts.length < 2) return;
      renderedLines++;

      const lineColor = colors[hit.paylineIndex % colors.length];
      const lineRoot = new Container();
      lineRoot.eventMode = 'none';

      const glow = new Graphics();
      glow.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) glow.lineTo(pts[i].x, pts[i].y);
      glow.stroke({
        width: u.winLineGlowWidth,
        color: lineColor,
        alpha: 0.28,
        cap: 'round',
        join: 'round',
      });

      const core = new Graphics();
      core.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) core.lineTo(pts[i].x, pts[i].y);
      core.stroke({
        width: u.winLineCoreWidth,
        color: lineColor,
        alpha: 0.95,
        cap: 'round',
        join: 'round',
      });

      const joints = new Graphics();
      for (const p of pts) {
        joints.circle(p.x, p.y, u.winLineJointRadius).fill({ color: lineColor, alpha: 0.85 });
      }

      lineRoot.addChild(glow, core, joints);

      const mx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const my = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      const amt = new Text({
        text: hit.payout.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        style: {
          fontFamily: t.fontUi,
          fontSize: 22,
          fontWeight: '800',
          fill: lineColor,
          dropShadow: {
            alpha: 0.85,
            angle: Math.PI / 2,
            blur: 4,
            color: 0x1a0a30,
            distance: 1,
          },
        },
      });
      amt.anchor.set(0.5, 0.5);
      amt.x = mx;
      amt.y = my - u.winLineLabelOffsetY - hitIndex * 26;
      lineRoot.addChild(amt);

      this.winOverlay.addChild(lineRoot);
    });

    if (renderedLines === 0) return;

    if (this.winOverlay.parent === this.root) {
      this.root.setChildIndex(this.winOverlay, this.root.children.length - 1);
    }

    if (animateEntry) {
      this.winOverlay.alpha = 0;
      this.animationController.runGsap(this.winOverlay, {
        alpha: 1,
        duration: 0.32,
        ease: 'power2.out',
        onComplete: () => {
          this.animationController.runGsap(this.winOverlay, {
            alpha: 0.88,
            duration: 0.85,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
          });
        },
      });
    } else {
      this.winOverlay.alpha = 1;
    }
  }

  /**
   * Draw paylines through winning cells and show per-line win amounts.
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hits
   */
  showPaylineWins(hits) {
    this._renderPaylineOverlayHits(hits, { animateEntry: true });
    this._playWinLineSound();
    const cells = this._uniqueWinCells(hits.flatMap((h) => h.cells));
    this._applyGridWinPresentation(cells);
    this._startWinClips(cells);
  }

  _playWinLineSound() {
    sound.play('winFrame', { allowOverlap: true });
  }

  stopWinLineCycle() {
    this._winLineCycleActive = false;
    if (this._winLineCycleDelay) {
      const tw = this._winLineCycleDelay;
      this._winLineCycleDelay = null;
      tw.kill();
      this.animationController.releaseTween(tw);
    }
    if (this._winLineCycleResolve) {
      const r = this._winLineCycleResolve;
      this._winLineCycleResolve = null;
      r();
    }
  }

  /**
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   * @returns {import('../logic/winEvaluator.js').CellRef[]}
   */
  _uniqueWinCells(cells) {
    const seen = new Set();
    const out = [];
    for (const c of cells) {
      const k = `${c.reel},${c.row}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
    return out;
  }

  /**
   * Seconds to wait for one payline’s win clips (longest symbol + left-to-right stagger).
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   */
  _lineWinPlaybackSec(cells) {
    const cfg = gameConfig.adapter;
    const minHold = cfg.winLinePhaseHoldSec ?? 0.92;
    const stagger = cfg.winStaggerSec ?? 0.045;
    const unique = this._uniqueWinCells(cells);
    if (!unique.length) return minHold;

    let maxDur = 0;
    for (const c of unique) {
      const sym = this.reels[c.reel]?.getSymbolAtVisibleRow(c.row);
      maxDur = Math.max(maxDur, sym?.getWinClipDurationSec?.() ?? 0);
    }
    const staggerPad = unique.length >= 2 ? (unique.length - 1) * stagger : 0;
    return Math.max(minHold, maxDur + staggerPad + 0.05);
  }

  /**
   * Cycles one winning payline at a time (line 1 → line 2 → … → repeat) until {@link prepareForNewRound}.
   * @param {import('../logic/winEvaluator.js').WinResult['hits']} hitsSorted sorted by `paylineIndex`
   * @param {number} totalWinAmount
   * @param {(totalWin: number, hit: import('../logic/winEvaluator.js').WinResult['hits'][number]) => void} onPhase
   */
  startWinLineCycle(hitsSorted, totalWinAmount, onPhase) {
    this.stopWinLineCycle();
    if (!hitsSorted.length) return;

    const n = hitsSorted.length;
    this._winLineCycleActive = true;

    const runPhase = async (index) => {
      while (this._winLineCycleActive) {
        const hit = hitsSorted[index];
        const lineCells = hit.cells ?? [];
        this._renderPaylineOverlayHits([hit], { animateEntry: false });
        this._playWinLineSound();
        this._applyGridWinPresentation(lineCells);
        onPhase?.(totalWinAmount, hit);
        await this._playLineWinClips(lineCells);
        if (!this._winLineCycleActive) return;
        index = (index + 1) % n;
      }
    };

    void runPhase(0);
  }

  /**
   * Blur / spin clip on every currently visible cell (e.g. error cleanup, no-matrix path).
   * Reel motion blur is driven per-strip in {@link Reel#spinToResult}.
   * @param {boolean} spinning
   */
  setAllVisibleSymbolsSpinning(spinning) {
    for (const reel of this.reels) {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = reel.getSymbolAtVisibleRow(r);
        if (sym) sym.setSpinning(spinning);
      }
    }
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   */
  beginPendingSpin() {
    this.clearStopRequest();
    this._applyReelsViewportMask(this._maskPadRelaxed, false);
    this.reels.forEach((reel) => reel.beginPendingSpin());
  }

  requestStop() {
    this._stopRequested = true;
    this.reels.forEach((reel) => reel.requestStop());
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   * @param {{ reelStopStaggerScale?: number, onReelLanded?: (col: number) => void }} [opts]
   *   `reelStopStaggerScale` multiplies `reelStopStaggerSec` (only stop spacing, not scroll speed).
   *   `onReelLanded(col)` fires once per reel as it locks — great for per-reel SFX.
   */
  async spinToGrid(grid, opts = {}) {
    const fastStopRequested = this._stopRequested;
    this._stopRequested = false;
    const { reelStopStaggerScale, onReelLanded } = opts;
    /** One random strip depth per spin — keeps reel-to-reel stop gaps aligned with stagger timing. */
    const landingOffset =
      gameConfig.stripLandingOffsetMin +
      Math.floor(Math.random() * gameConfig.stripLandingOffsetSpread);
    const extraCells =
      gameConfig.extraSpinCellsMin + Math.floor(Math.random() * gameConfig.extraSpinCellsSpread);
    const travelBase = { landingOffset, extraCells };

    const tasks = this.reels.map((reel, col) => {
      const colResult = [
        grid[col][0],
        grid[col][1],
        grid[col][2],
      ];
      const p = reel.spinToResult(colResult, {
        columnIndex: col,
        fastStop: fastStopRequested,
        reelStopStaggerScale,
        travelBase,
      });
      if (typeof onReelLanded === 'function') {
        p.then(() => {
          try { onReelLanded(col); } catch (_e) { /* never let SFX break the spin */ }
        });
      }
      return p;
    });
    sound.play('reelSpin');
    await Promise.all(tasks);
    // After landing: tight top again so idle strip does not peek above row 0.
    this._applyReelsViewportMask(this._maskPadRelaxed, true);
  }

  /**
   * Animate backend expanding-wild columns after base spin landed.
   * @param {number[]} expandingWild per reel, 0 means none; >0 means wild symbol id
   */
  async animateExpandingWildColumns(expandingWild) {
    if (!Array.isArray(expandingWild) || !expandingWild.length) return;
    const wildReels = [];
    for (let reel = 0; reel < Math.min(expandingWild.length, this.reels.length); reel++) {
      const wildSymbolId = Number(expandingWild[reel]);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;
      wildReels.push({ reel, wildSymbolId });
    }
    if (!wildReels.length) return;

    sound.play('expandingWilds', { allowOverlap: true });

    await Promise.all(
      wildReels.map(({ reel, wildSymbolId }) => {
        const targetReel = this.reels[reel];
        return new Promise((resolve) => {
          const tl = gsap.timeline();
          this.animationController.trackTween(tl);
          const reelX = targetReel.root.x;
          const reelCenterY = (gameConfig.rowCount * gameConfig.cellHeight) / 2;
          const sidePad = Math.max(8, Math.round(gameConfig.cellWidth * 0.12));
          const leftVine = createVineSpineInstance(this.spineAssets, 'left');
          const rightVine = createVineSpineInstance(this.spineAssets, 'right');
          const cleanupVines = () => {
            for (const vine of [leftVine, rightVine]) {
              if (!vine) continue;
              vine.parent?.removeChild(vine);
              vine.destroy({ children: true });
            }
          };
          const playVineLoop = (vine) => {
            if (!vine?.state || !vine?.skeleton?.data) return;
            const data = vine.skeleton.data;
            const idle = data.findAnimation('idle') ? 'idle' : null;
            const start = data.findAnimation('start') ? 'start' : null;
            if (!idle && !start) return;
            vine.state.clearTracks();
            if (idle) {
              const idleEntry = vine.state.setAnimation(0, idle, true);
              if (idleEntry) idleEntry.mixDuration = 0;
            }
            if (start) {
              const startEntry = vine.state.setAnimation(1, start, false);
              if (startEntry) startEntry.mixDuration = 0;
            }
          };
          if (leftVine) {
            leftVine.visible = false;
            leftVine.position.set(reelX + gameConfig.cellWidth + sidePad - 20, reelCenterY);
            this.expandingWildFxLayer.addChild(leftVine);
            playVineLoop(leftVine);
            leftVine.visible = true;
          }
          if (rightVine) {
            rightVine.visible = false;
            rightVine.position.set(reelX - sidePad + 20, reelCenterY);
            this.expandingWildFxLayer.addChild(rightVine);
            playVineLoop(rightVine);
            rightVine.visible = true;
          }
          let settled = false;
          const settle = () => {
            if (settled) return;
            settled = true;
            setTimeout(() => {
              cleanupVines();
              resolve();
            }, 800);
          };
          tl.eventCallback('onComplete', settle);
          tl.eventCallback('onInterrupt', settle);
          for (let row = 0; row < gameConfig.rowCount; row++) {
            const sym = targetReel.getSymbolAtVisibleRow(row);
            tl.to(
              sym.root.scale,
              {
                x: 1.12,
                y: 1.12,
                duration: 0.09,
                ease: 'power2.out',
              },
              row * 0.05,
            ).add(() => {
              targetReel.setVisibleRowSymbolId(row, wildSymbolId);
            });
            tl.to(sym.root.scale, {
              x: 1,
              y: 1,
              duration: 0.11,
              ease: 'power2.in',
            });
          }
        });
      }),
    );
  }

  /**
   * @param {number[][]} grid column-major [reel][row]
   */
  setGridInstant(grid) {
    this.reels.forEach((reel, col) => {
      const colResult = [
        grid[col][0],
        grid[col][1],
        grid[col][2],
      ];
      reel.setResultInstant(colResult);
    });
    this._applyReelsViewportMask(this._maskPadRelaxed);
  }

  /** Return lifted win symbols to their reels (only symbols on the lift layer). */
  _lowerAllLiftedSymbols() {
    for (const reel of this.reels) {
      for (const sym of reel._stripSymbols) {
        if (sym.root?.parent === this._symbolWinLiftLayer) {
          this._lowerSymbolFromWin(sym, reel);
        }
      }
    }
  }

  /**
   * One-shot cleanup when a new round starts: stop win cycling, overlays, tweens, and idle symbols.
   * Called from {@link SlotMachine#prepareForNewRound} before reels spin.
   */
  prepareForNewRound() {
    this.stopWinLineCycle();
    this.clearPaylineOverlay();
    gsap.killTweensOf(this._symbolWinLiftLayer);
    this._lowerAllLiftedSymbols();
    for (const reel of this.reels) {
      gsap.killTweensOf(reel.stripContainer);
      reel.resetForNewRound();
    }
  }

  /** @deprecated Use {@link #prepareForNewRound} */
  clearWinState() {
    this.prepareForNewRound();
  }

  /**
   * Dim non-winners; lift winners. Does not start Spine clips — see {@link #_startWinClips}.
   * @param {import('../logic/winEvaluator.js').CellRef[]} winCells
   */
  _applyGridWinPresentation(winCells) {
    const winSet = new Set(winCells.map((c) => `${c.reel},${c.row}`));

    this.reels.forEach((reel, col) => {
      for (let r = 0; r < gameConfig.rowCount; r++) {
        const sym = reel.getSymbolAtVisibleRow(r);
        const isWin = winSet.has(`${col},${r}`);
        sym.setWinPresentation(isWin ? 'highlight' : 'dim');
        if (isWin) {
          this._liftSymbolForWin(sym, reel);
        } else {
          this._lowerSymbolFromWin(sym, reel);
        }
      }
    });
  }

  /**
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   */
  _startWinClips(cells) {
    const unique = this._uniqueWinCells(cells);
    const stagger = gameConfig.adapter.winStaggerSec ?? 0.045;

    const play = (c) => this.reels[c.reel].getSymbolAtVisibleRow(c.row)?.playWin?.();

    if (stagger <= 0 || unique.length < 2) {
      for (const c of unique) play(c);
      return;
    }

    const tl = this.animationController.createTimeline();
    unique.forEach((c, i) => {
      tl.call(() => play(c), null, i * stagger);
    });
  }

  /**
   * One payline: play each symbol’s `win` once from the start, then resolve when the full line finishes.
   * @param {import('../logic/winEvaluator.js').CellRef[]} cells
   * @returns {Promise<void>}
   */
  _playLineWinClips(cells) {
    return new Promise((resolve) => {
      const unique = this._uniqueWinCells(cells);
      if (!unique.length) {
        resolve();
        return;
      }

      const stagger = gameConfig.adapter.winStaggerSec ?? 0.045;
      const play = (c) => this.reels[c.reel].getSymbolAtVisibleRow(c.row)?.playWinOnce?.();

      if (stagger <= 0 || unique.length < 2) {
        for (const c of unique) play(c);
      } else {
        const tl = this.animationController.createTimeline();
        unique.forEach((c, i) => {
          tl.call(() => play(c), null, i * stagger);
        });
      }

      const totalSec = this._lineWinPlaybackSec(cells);
      this._winLineCycleResolve = resolve;
      const wait = gsap.delayedCall(totalSec, () => {
        this._winLineCycleDelay = null;
        this._winLineCycleResolve = null;
        this.animationController.releaseTween(wait);
        resolve();
      });
      this._winLineCycleDelay = wait;
      this.animationController.trackTween(wait);
    });
  }

  /**
   * For debug overlay: label positions in this container's space.
   * @returns {Array<{ x: number, y: number, text: string }>}
   */
  getDebugCellLabels() {
    const out = [];
    const step = gameConfig.cellWidth + gameConfig.reelGap;
    const ch = gameConfig.cellHeight;
    for (let col = 0; col < gameConfig.reelCount; col++) {
      const reel = this.reels[col];
      const li = reel.getLandIndex();
      for (let row = 0; row < gameConfig.rowCount; row++) {
        out.push({
          x: col * step + 4,
          y: row * ch + 4,
          text: `R${col}↕${li + row}`,
        });
      }
    }
    return out;
  }
}
